import React, { useEffect, useRef, useState } from 'react';
import { CheckoutButton } from './CheckoutButton';
import { LIGHTBOX_EVENT } from './Lightbox/Lightbox';
import { PRICE } from '../data/checkout';

/** Elements that make the bar redundant while they are on screen. */
const HIDE_WHEN_VISIBLE = ['offer-cta', 'final-cta', 'site-footer'];
const inView = (id: string) => {
  const el = document.getElementById(id);
  if (!el) return false;
  const rect = el.getBoundingClientRect();
  return rect.top < window.innerHeight && rect.bottom > 0;
};

/**
 * Mobile-only purchase bar. Dormant until the visitor has actually seen the
 * offer price; afterwards it shows unless another buy button, the footer or
 * the page viewer is on screen. Uses the same purchase destination as the offer.
 */
export function StickyBuyBar() {
  const [visible, setVisible] = useState(false);
  const seenOffer = useRef(false);
  const lightboxOpen = useRef(false);
  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      if (!seenOffer.current && inView('offer-price')) seenOffer.current = true;
      setVisible(seenOffer.current && !lightboxOpen.current && !HIDE_WHEN_VISIBLE.some(inView));
    };
    const schedule = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };
    const onLightbox = (event: Event) => {
      lightboxOpen.current = Boolean((event as CustomEvent<boolean>).detail);
      schedule();
    };
    update();
    window.addEventListener('scroll', schedule, {
      passive: true
    });
    window.addEventListener('resize', schedule);
    window.addEventListener(LIGHTBOX_EVENT, onLightbox);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      window.removeEventListener(LIGHTBOX_EVENT, onLightbox);
    };
  }, []);
  return <div aria-hidden={!visible} className={`fixed inset-x-3 z-40 transition-[transform,opacity] duration-[250ms] md:hidden ${visible ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-[calc(100%+24px)] opacity-0'}`} style={{
    bottom: 'calc(12px + env(safe-area-inset-bottom))',
    transitionTimingFunction: 'cubic-bezier(0.23, 1, 0.32, 1)'
  }} data-id="element-244" data-mp="1bqpgqngilkk6">
      <div className="flex items-center justify-between gap-3 rounded-[14px] bg-charcoal-raised py-2 pl-4 pr-2 shadow-[0_20px_40px_-12px_rgba(0,0,0,0.7)] ring-1 ring-white/10" data-id="element-245" data-mp="2k0q0kh1ioasn">
        <p className="min-w-0 leading-tight" data-id="element-246" data-mp="2t1xmdzmujj5u">
          <span className="wordmark block truncate text-[0.72rem] text-ivory" data-id="element-247" data-mp="2t0utjm5yr6na">
            Woodworking Furniture Plans<sup className="font-sans text-[0.5em]" data-id="element-248" data-mp="37q0vvj03lcze">™</sup>
          </span>
          <span className="sr-only" data-id="element-249" data-mp="32sihve7847kj"> · </span>
          <span className="mt-1 block font-brand text-[1.2rem] font-bold text-ivory" data-id="element-250" data-mp="32ttlne0o5fbn">{PRICE.label}</span>
        </p>
        <CheckoutButton label="GET IT NOW" size="sm" className="shrink-0" tabIndex={visible ? 0 : -1} data-id="element-251" data-mp="3bs5nydawe1jx" />
      </div>
    </div>;
}